import { correctEdges, type Board } from '../puzzle';
import type { Rect } from '../play/layout';

export type Sides<T> = { l: T; r: T; t: T; b: T };
export type UVRect = { u0: number; v0: number; u1: number; v1: number };

/** 每块四条边是否与正确邻居相连（相连的边应合缝） */
export function sideClosure(board: Board): Sides<boolean>[] {
  const out = board.pos.map(() => ({ l: false, r: false, t: false, b: false }));
  for (const [a, b] of correctEdges(board)) {
    if (b === a + 1) {
      out[a].r = true;
      out[b].l = true;
    } else {
      out[a].b = true;
      out[b].t = true;
    }
  }
  return out;
}

/** n = 列数，rows = 行数（缺省 = n） */
export function tileUV(tile: number, n: number, rows = n): UVRect {
  const row = Math.floor(tile / n);
  const col = tile % n;
  // 写成 (rows-1-row)/rows 而不是 1-(row+1)/rows，避免浮点误差（测试用 toEqual 精确比较）
  return { u0: col / n, u1: (col + 1) / n, v0: (rows - 1 - row) / rows, v1: (rows - row) / rows };
}

/** closure 0=开缝 1=合缝；返回每边内缩像素 */
export function insetsFrom(closure: Sides<number>, gap: number): Sides<number> {
  const h = gap / 2;
  return { l: h * (1 - closure.l), r: h * (1 - closure.r), t: h * (1 - closure.t), b: h * (1 - closure.b) };
}

/** PlaneGeometry 顶点顺序（左上、右上、左下、右下），坐标相对格子中心 */
export function quadFor(cell: Rect, uv: UVRect, inset: Sides<number>): { positions: number[]; uvs: number[] } {
  const w = cell.x1 - cell.x0;
  const h = cell.y1 - cell.y0;
  const cx = (cell.x0 + cell.x1) / 2;
  const cy = (cell.y0 + cell.y1) / 2;
  const x0 = cell.x0 + inset.l - cx;
  const x1 = cell.x1 - inset.r - cx;
  const y0 = cell.y0 + inset.b - cy;
  const y1 = cell.y1 - inset.t - cy;
  const du = uv.u1 - uv.u0;
  const dv = uv.v1 - uv.v0;
  const u0 = uv.u0 + (inset.l / w) * du;
  const u1 = uv.u1 - (inset.r / w) * du;
  const v0 = uv.v0 + (inset.b / h) * dv;
  const v1 = uv.v1 - (inset.t / h) * dv;
  return {
    positions: [x0, y1, 0, x1, y1, 0, x0, y0, 0, x1, y0, 0],
    uvs: [u0, v1, u1, v1, u0, v0, u1, v0],
  };
}
